import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Typography, Empty, Tag } from "antd";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";

const { Title, Text } = Typography;
const MS_DAY = 86400000;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const formatOccurredAt = (iso) => {
  if (!iso) return { label: "--", sub: null, recent: false };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { label: "--", sub: null, recent: false };

  const now = new Date();
  const dayDiff = Math.floor((startOfDay(now) - startOfDay(date)) / MS_DAY);
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  if (dayDiff === 0) return { label: "Today", sub: time, recent: true };
  if (dayDiff === 1) return { label: "Yesterday", sub: time, recent: true };
  if (dayDiff < 7) {
    return {
      label: dayDiff === 2 ? "2 days ago" : `${dayDiff} days ago`,
      sub: time,
      recent: true,
    };
  }

  return {
    label: date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }),
    sub: null,
    recent: false,
  };
};

const TYPE_META = {
  weekly_accountability: { label: "Weekly check-in", color: "blue" },
  accountability: { label: "Accountability", color: "geekblue" },
  text: { label: "Text", color: "default" },
};

const formatTypeMeta = (type) => {
  if (!type || type === "--") return { label: "--", color: "default" };
  const known = TYPE_META[type];
  if (known) return known;
  const label = String(type)
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return { label, color: "processing" };
};

const getUserIdFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return params.get("userId");
};

const getPlayerNameFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return params.get("playerName") || "Player";
};

const getHistoryItems = (payload) => {
  const raw = payload?.data ?? payload;
  const inner = raw?.data !== undefined ? raw.data : raw;
  return Array.isArray(inner?.items) ? inner.items : [];
};

const toRows = (items) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    key: item.eventId,
    occurredAt: item.occurredAt,
    step: item.stepNumber ? `Day ${item.stepNumber}` : "--",
    responseType: item.responseType || "--",
    responseBody: item.responseBody || "",
  }));

const DateCell = ({ occurredAt }) => {
  const { label, sub, recent } = formatOccurredAt(occurredAt);
  return (
    <div>
      <Text strong={recent}>{label}</Text>
      {sub ? (
        <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
          {sub}
        </Text>
      ) : null}
    </div>
  );
};

const TypePill = ({ type }) => {
  const { label, color } = formatTypeMeta(type);
  if (label === "--") return <Text type="secondary">--</Text>;
  return (
    <Tag color={color} style={{ margin: 0, borderRadius: 999, paddingInline: 10 }}>
      {label}
    </Tag>
  );
};

const PlayerAccountabilityHistory = ({ fetchAccountabilityHistory }) => {
  const location = useLocation();
  const userId = useMemo(() => getUserIdFromSearch(location.search), [location.search]);
  const playerName = useMemo(() => getPlayerNameFromSearch(location.search), [location.search]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    fetchAccountabilityHistory({
      params: { userId, limit: 100 },
      callback: (ok, response) => {
        if (ok) {
          setRows(toRows(getHistoryItems(response)));
        } else {
          setRows([]);
        }
        setLoading(false);
      },
    });
  }, [userId]);

  const columns = [
    {
      title: "Date",
      dataIndex: "occurredAt",
      key: "occurredAt",
      width: 160,
      render: (occurredAt) => <DateCell occurredAt={occurredAt} />,
    },
    { title: "Cycle Day", dataIndex: "step", key: "step", width: 110 },
    {
      title: "Type",
      dataIndex: "responseType",
      key: "responseType",
      width: 160,
      render: (type) => <TypePill type={type} />,
    },
    { title: "Accountability Post", dataIndex: "responseBody", key: "responseBody" },
  ];

  return (
    <div className="w-[98%] p-[2%]">
      <Title level={3} style={{ marginBottom: 2 }}>Accountability History</Title>
      <Text type="secondary">{playerName}</Text>
      <Card className="mt-4">
        {rows.length === 0 && !loading ? (
          <Empty description="No accountability posts yet" />
        ) : (
          <Table
            bordered
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 20 }}
          />
        )}
      </Card>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  fetchAccountabilityHistory: (details) =>
    dispatch(global.redux.action.learning_accountability.historyForPlayer(details)),
});

export default connect(null, mapDispatchToProps)(PlayerAccountabilityHistory);
